import React, { cloneElement } from 'react';
import { DateTime } from 'luxon';
import { makeStyles, Tooltip } from '@material-ui/core';
import { useLocalization } from '@fluent/react';
import { useDateTime } from '../../../Localization/useDateTime';
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date"
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
    // This is hardcode style to prevent date picker days size changing on resize
    cell: {
        width: '32px',
        height: '32px',
        margin: '2px'
    },
    closed: {
        background: theme.palette.error.light,
        color: theme.palette.common.white,
        '&:hover, &:focus': {
            backgroundColor: `${theme.palette.error.light} !important`,
        },
    },
    notAllowedWdd: {
        background: theme.palette.text.disabled,
        color: theme.palette.common.white,
        '&:hover, &:focus': {
            backgroundColor: `${theme.palette.text.disabled} !important`,
        },
    },
    past: {
        pointerEvents: 'none',
        color: theme.palette.text.disabled,
        backgroundColor: 'transparent',
        '&:hover, &:focus': {
            backgroundColor: 'transparent',
        },
    },
    active: {
        '&:hover': {
            backgroundColor: `rgba(0, 144, 255, 0.15) !important`,
        },
    },
    selected: {
        backgroundColor: `${theme.palette.primary.main} !important`,
        color: `${theme.palette.common.white} !important`,
        '&:hover, &:focus': {
            backgroundColor: `rgba(0, 144, 255) !important`,
        },
    },
}));

interface ICustomDayProps {
    day: MaterialUiPickersDate;
    selectedDate: MaterialUiPickersDate;
    closingDates: string[];
    allowedWeekdays: number[];
    dayComponent: React.ReactElement;
}

export function CustomDay({
                              day,
                              selectedDate,
                              closingDates,
                              allowedWeekdays,
                              dayComponent,
                          }: ICustomDayProps) {
    const classes = useStyles();
    const { l10n } = useLocalization();
    const factory = useDateTime();

    if (!day) return dayComponent;

    const toDateTime = (d: MaterialUiPickersDate): DateTime | undefined => {
        if (!d) return undefined;

        return d || DateTime.fromJSDate(d as Date)
    }

    const dt = toDateTime(day);
    if (!dt) return dayComponent;

    const today = factory.now().startOf('day');
    const closedDTs = closingDates.map(d => DateTime.fromFormat(d, 'yyyy.MM.dd'));
    const isClosed = closedDTs.some(d => d.hasSame(dt, 'day'));
    const notAllowedWdd = !allowedWeekdays.includes(dt.weekday);
    const isPast = dt < today;
    const unavailable = isClosed || notAllowedWdd;
    const disabled = unavailable || isPast;
    const selectedDT = toDateTime(selectedDate || null)
    const isSelected = !!selectedDT && selectedDT.hasSame(dt, 'day');

    return (
        <Tooltip
            PopperProps={{
                modifiers: {
                    offset: { offset: '0, -18' },
                },
            }}
            title={
                unavailable
                    ? l10n.getString('unavailable-date-tooltip', undefined, 'Denne dato kan ikke vælges')
                    : ''
            }
            arrow
        >
            {cloneElement(dayComponent, {
                disabled,
                className: clsx(
                    dayComponent.props.className,
                    classes.cell,
                    isClosed && !notAllowedWdd && classes.closed,
                    !unavailable && isPast && classes.past,
                    !isClosed && notAllowedWdd && classes.notAllowedWdd,
                    !unavailable && !isPast && !isSelected && classes.active,
                    isSelected && classes.selected
                ),
            })}
        </Tooltip>
    );
}
